import React, { useCallback, useMemo, useState } from "react";
import { ProductProps } from "../../../../1_types/productTypes.js";
import {
  GetOneMarketingCampaignConfig,
  ThresholdMarketingCampaignConfig,
} from "../../../../1_types/genericTypes.js";
import { ProductCard } from "@dishopsaas/design-system";
import { useAppDispatch, useAppSelector } from "../../../../redux/hook";
import {
  getMinimumPriceFreeProduct,
  isProductDisabled,
  isStringNotNull,
  updateOrderService,
} from "../../../../utils";
import { getProductTempPrice } from "../../../../utils/offer";
import { IMAGE_UNAVAILABLE_IMAGE_URL } from "../../../../constants";
import {
  getTooltipTextFromOffer,
  getNbProductForAnOriginalProduct,
  handleClickCard,
  getCurrentOfferForProduct,
  getOfferProductsNbInPendingOrderBasket,
  getDisabledMessage,
} from "./Product.services";
import ProductDetailModalView from "./ProductDetailModal/ProductDetailModal.view";
import {
  areNumbersDifferent,
  convertPotentialStringToNumber,
} from "../../../../utils/number";
import { isObjectDefined } from "../../../../utils/object";

export default function Product({
  product,
  cardImage,
  sectionKey,
  sectionName,
  messagesSectionRange,
  isVertical,
  isOfferSection,
  isLoading,
}: ProductProps) {
  const dispatch = useAppDispatch();
  const { key, name, description, price } = product || {};
  const numericPrice = convertPotentialStringToNumber(price);
  const pendingOrder = useAppSelector((s) => s.pendingOrderReducer);
  const { sections, categories: shopCategories } = useAppSelector(
    (s) => s.shopReducer,
  );
  const { multiOrder, generalDesign, disableProductInstruction } =
    useAppSelector((s) => s.configurationReducer);
  const { thresholdOffer, productOffers, bngmOffers } = useAppSelector(
    (state) => state.offerReducer,
  );
  const { customerAddressCheck } = useAppSelector((s) => s.componentReducer);
  const { orders } = useAppSelector((s) => s.orderReducer);
  const [showModal, setShowModal] = useState(false);
  const layout = isVertical ? "vertical" : "horizontal";

  if (isLoading) {
    return (
      <ProductCard loading layout={isVertical ? "vertical" : "horizontal"} />
    );
  }

  if (!product) return null;

  const section = useMemo(
    () => sections.find((s) => s.key === sectionKey),
    [sections, sectionKey],
  );
  const productOffer = useMemo(() => {
    if (product.isBngmOffer && product.bngmConfig?.offerId) {
      const bngmOffer =
        bngmOffers?.find(o => o.key === product.bngmConfig!.offerId) ?? null;

      return bngmOffer;
    }

    return getCurrentOfferForProduct(
      product.key,
      sectionKey,
      thresholdOffer,
      productOffers,
      isOfferSection,
    );
  }, [section?.key, thresholdOffer, productOffers, isOfferSection, bngmOffers, product.isBngmOffer]);
  const potentialLimit = (productOffer?.config as GetOneMarketingCampaignConfig)
    ?.limit;
  const offerProductsNbInPendingOrderBasket = getOfferProductsNbInPendingOrderBasket(
    pendingOrder?.commande,
    productOffer?.key,
  );
  const isLimitReached = isObjectDefined(potentialLimit) && Number(potentialLimit) <= offerProductsNbInPendingOrderBasket
  const hideProductName =
    section?.hideProductName || generalDesign?.hideProductsName?.products;
  const potentialThreshold = (
    productOffer?.config as ThresholdMarketingCampaignConfig
  )?.threshold;
  const thresholdNotReached =
    isObjectDefined(potentialThreshold) &&
    (pendingOrder?.totalProductPriceWithPromo ?? 0) < potentialThreshold;
  const isDisabled =
    isProductDisabled(product) || messagesSectionRange?.[section?.key];
  const productDisabled = isDisabled || thresholdNotReached || isLimitReached;
  const imageUrl = isStringNotNull(cardImage)
    ? cardImage
    : IMAGE_UNAVAILABLE_IMAGE_URL;

  const minimumPrice = useMemo(
    () => getMinimumPriceFreeProduct(product),
    [product],
  );

  const promoPrice = (() => {
    if (!productOffer) return null;

    const basePrice = Number(numericPrice ?? minimumPrice);
    const tempPrice = getProductTempPrice(
      { ...product, price: basePrice },
      productOffer,
      pendingOrder,
    );

    return areNumbersDifferent(tempPrice, basePrice)
      ? tempPrice
      : null;
  })();
  const finalPrice =
    promoPrice ?? (numericPrice > 0 ? numericPrice : minimumPrice);
  const displayPrice = Math.max(finalPrice, 0);
  const oldPrice = promoPrice ? numericPrice || minimumPrice : undefined;

  const nbAlreadyInOrder = useMemo(
    () =>
      getNbProductForAnOriginalProduct(
        pendingOrder?.commande,
        sectionKey,
        product.key,
        productOffer?.key ?? null,
      ),
    [pendingOrder?.commande, sectionKey, product.key, productOffer?.key],
  );

  const hasOptions = product.categories?.length > 0 || product.offeredKey;

  const updateOrder = useCallback(
    async (quantity?: number) => {
      await updateOrderService({
        setShowModal,
        product,
        productItems: null,
        instruction: null,
        nbProduct: quantity,
        sectionName,
        sectionKey,
        pendingOrder,
        productOffer,
        key,
      });
    },
    [pendingOrder, product, key, productOffer, sectionKey, sectionName],
  );

  const onCardClick = useCallback(
    () =>
      handleClickCard({
        dispatch,
        setShowModal,
        productDisabled,
        customerAddressCheck,
        customerAddress: pendingOrder?.address,
        orders,
        multiOrder,
        key: product.key,
        price: numericPrice,
        position: product.position,
        section,
        name: product.name,
      }),
    [
      dispatch,
      productDisabled,
      customerAddressCheck,
      pendingOrder?.address,
      orders,
      multiOrder,
      product,
      numericPrice,
      section,
    ],
  );
  const promoText = getTooltipTextFromOffer(productOffer);
  const handleQuantityChange = useCallback(
    (qty: number) => {
      if (hasOptions) {
        setShowModal(true);
      } else {
        updateOrder(qty);
      }
    },
    [hasOptions, updateOrder],
  );

  const disabledMessage = getDisabledMessage(
    productOffer,
    potentialThreshold,
    isDisabled,
    isLimitReached,
    potentialLimit,
    thresholdNotReached,
  );
  const prefix = price <= 0 && minimumPrice > 0 ? "À partir de " : undefined;
  return (
    <>
      <ProductCard
        image={imageUrl}
        title={hideProductName ? " " : name}
        description={description}
        descriptionLines={2}
        price={productDisabled ? oldPrice : displayPrice}
        pricePrefix={prefix}
        oldPrice={productDisabled ? undefined : oldPrice}
        promoLabel={promoText?.title}
        layout={layout}
        withCounter={nbAlreadyInOrder > 0}
        quantity={nbAlreadyInOrder}
        disabled={productDisabled}
        unavailableLabel={disabledMessage?.text}
        onClick={onCardClick}
        onQuantityChange={handleQuantityChange}
        forceShowAddButton={hasOptions}
        loading={isLoading}
      />

      <ProductDetailModalView
        showModal={showModal}
        disableProductInstruction={disableProductInstruction}
        price={numericPrice}
        minimumPrice={minimumPrice}
        productOffer={productOffer}
        sectionKey={sectionKey}
        shopCategories={shopCategories}
        pendingOrder={pendingOrder}
        product={product}
        setShowModal={setShowModal}
        sectionName={sectionName}
        onClose={() => setShowModal(false)}
        promoText={promoText}
        disabledMessage={disabledMessage}
        offerProductsNbInPendingOrderBasket={offerProductsNbInPendingOrderBasket}
        potentialLimit={potentialLimit}
      />
    </>
  );
}
